import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { normalizeDeliveryType } from '../utils/deliveryHelpers';
import FroggySpinner from '../components/common/FroggySpinner';
import '../styles/shipping-calendar.css';
import '../styles/fast-tooltips.css';
import '../styles/customer-schedule.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Temperature safety constants - KEEP IN SYNC with backend validation
// See: frogger1/frogger/store/utils/weather_thresholds.py (the source of
// truth) and store/views.py _windowed_date_gate, which reads it.
//
// Residential HOT limits are PER LEG as of 2026-08-22. A hot evening at
// origin is a brief handoff into a cooled truck; the destination is where
// the box sits unattended, so it keeps the tighter 85. The backend decides
// availability — these values only drive the explanatory copy, and showing
// one blanket "unsafe above 85" made an 89°F origin look like the blocker
// when it no longer is.
const TEMP_LIMITS = {
  RESIDENTIAL_MIN: 40,
  RESIDENTIAL_MAX_ORIGIN: 85,
  RESIDENTIAL_MAX_MIDPOINT: 85,
  RESIDENTIAL_MAX_DESTINATION: 85,
  // Strictest value, for copy that isn't leg-specific. Never the loosest.
  RESIDENTIAL_MAX: 85,
  HUB_MIN: 30,
  HUB_MAX: 92,
  ABSOLUTE_MIN: 29,
  ABSOLUTE_MAX: 93,
};

// Hot ceiling for one leg. Unknown legs get the strictest value, matching
// residential_hot_limit() on the backend.
const RESIDENTIAL_HOT_BY_LEG = {
  origin: TEMP_LIMITS.RESIDENTIAL_MAX_ORIGIN,
  midpoint: TEMP_LIMITS.RESIDENTIAL_MAX_MIDPOINT,
  destination: TEMP_LIMITS.RESIDENTIAL_MAX_DESTINATION,
};
// hasOwn, not `?? `: bracket access on an object literal reaches prototype
// keys, so residentialHotLimit('constructor') returned a Function and
// `temp > Function` is always false — i.e. it failed OPEN, blocking nothing.
const residentialHotLimit = (leg) =>
  (Object.hasOwn(RESIDENTIAL_HOT_BY_LEG, leg)
    ? RESIDENTIAL_HOT_BY_LEG[leg]
    : Math.min(...Object.values(RESIDENTIAL_HOT_BY_LEG)));

// mode: 'residential' | 'hub'
function checkTemperatureSafety(dateISO, mode, stateRef) {
  // assumes we already have availability/forecast in memory; if not, accept a temps object param instead
  const dateDetails = stateRef?.current?.datesWithDetails?.find(d => d.date === dateISO)
    || null;
  if (!dateDetails?.temperatures) return { safe: false, reason: 'no-data' };
  const { origin, midpoint, destination } = dateDetails.temperatures;
  const legs = [origin, midpoint, destination].filter(Boolean);
  if (!legs.length) return { safe: false, reason: 'no-data' };

  const isHub = mode === 'hub';
  const minLimit = isHub ? TEMP_LIMITS.HUB_MIN : TEMP_LIMITS.RESIDENTIAL_MIN;

  // Block if ANY leg is out of range. Named legs, not an anonymous list —
  // the residential hot ceiling depends on WHICH leg it is.
  const namedLegs = [['origin', origin], ['midpoint', midpoint], ['destination', destination]];
  for (const [legName, leg] of namedLegs) {
    if (!leg) continue;
    const maxLimit = isHub ? TEMP_LIMITS.HUB_MAX : residentialHotLimit(legName);
    if (typeof leg.high === 'number' && leg.high > maxLimit) return { safe: false, reason: 'high' };
    if (typeof leg.low === 'number' && leg.low < minLimit)  return { safe: false, reason: 'low'  };
  }
  // Absolute hard stop (safety net)
  for (const leg of legs) {
    if (typeof leg.high === 'number' && leg.high >= TEMP_LIMITS.ABSOLUTE_MAX) return { safe: false, reason: 'abs-high' };
    if (typeof leg.low === 'number' && leg.low <= TEMP_LIMITS.ABSOLUTE_MIN)   return { safe: false, reason: 'abs-low'  };
  }
  return { safe: true, reason: 'ok' };
}

const CustomerSchedule = () => {
  // Punch #44b: this page serves TWO routes —
  //   /account/orders/:orderId/schedule  (account mode, auth required)
  //   /schedule/:token                   (token mode, capability link for
  //                                       account-less manual-order customers)
  const { orderId: orderIdParam, token: schedulingToken } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useContext(AuthContext);
  const [resolvedOrderId, setResolvedOrderId] = useState(null);
  const [tokenInvalid, setTokenInvalid] = useState(false);
  const [tokenScheduleSuccess, setTokenScheduleSuccess] = useState(null); // formatted date string
  const orderId = orderIdParam || resolvedOrderId;
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);
  const [datesWithDetails, setDatesWithDetails] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [orderStatus, setOrderStatus] = useState('');
  const [facilityName, setFacilityName] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [weatherEnabled, setWeatherEnabled] = useState(false);
  const [locationInfo, setLocationInfo] = useState({});
  const [requiresHubSelection, setRequiresHubSelection] = useState(false);
  const [availableHubs, setAvailableHubs] = useState([]);
  const [selectedHub, setSelectedHub] = useState(null);
  const [loadingHubs, setLoadingHubs] = useState(false);
  const [touchTooltipActive, setTouchTooltipActive] = useState(null);
  const [clickCount, setClickCount] = useState({});
  const [sessionExpired, setSessionExpired] = useState(false);
  const [isSubmittingRef, setIsSubmittingRef] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [orderData, setOrderData] = useState(null);
  const [selectedDeliveryType, setSelectedDeliveryType] = useState(null);
  const [selectedHubId, setSelectedHubId] = useState(null);
  const [savedIsHub, setSavedIsHub] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(null);
  
  // Stale request guards for hub fetching
  const hubsReqSeqRef = useRef(0);
  const hubsAbortRef = useRef(null);
  const isMounted = useRef(true);
  
  // State reference for temperature checking
  const stateRef = useRef();

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
      hubsAbortRef.current?.abort();
    };
  }, []);

  // Add body class for scroll fixes and styling
  useEffect(() => {
    document.body.classList.add('customer-schedule-page');
    return () => {
      document.body.classList.remove('customer-schedule-page');
    };
  }, []);

  // Token mode: resolve the capability token to an order id first
  useEffect(() => {
    if (!schedulingToken) return;
    (async () => {
      try {
        const resp = await fetch(`${API_URL}/store/schedule/token/${schedulingToken}/`);
        if (!resp.ok) throw new Error('Invalid scheduling link');
        const data = await resp.json();
        setResolvedOrderId(data.order_id);
      } catch (err) {
        console.warn('[CustomerSchedule] Scheduling token resolve failed');
        setTokenInvalid(true);
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedulingToken]);

  useEffect(() => {
    if (schedulingToken) {
      // Token mode: no login required — wait for the token to resolve
      if (orderId) loadDataSequentially();
      return;
    }
    if (!isAuthenticated) {
      // Redirect to login with return URL
      navigate(`/login?redirect=/account/orders/${orderId}/schedule`);
      return;
    }
    // Run sequentially to avoid race conditions with state
    loadDataSequentially();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, orderId, navigate, schedulingToken]);

  // Shared fetch helpers for both modes: only send Authorization when a real
  // auth token exists (token-mode visitors have none — a "Bearer null"
  // header would 401 at the auth layer before the capability token is seen).
  // M1 (security review): the capability token travels via the
  // X-Scheduling-Token header (GETs) and the POST body — never in the query
  // string, so it stays out of URL/access logs.
  const buildAuthHeaders = () => {
    // Token mode: the capability token IS the credential — never attach
    // the stored JWT alongside it. These are raw fetches with no refresh
    // interceptor, so a STALE Bearer is present-but-invalid and DRF 401s
    // before the capability token is even read (Run 20: the operator
    // previewing a customer's link got "may belong to a different
    // account" from exactly this; guests, with no JWT, worked fine).
    if (schedulingToken) {
      return { 'X-Scheduling-Token': schedulingToken };
    }
    const authToken = localStorage.getItem('authToken');
    return {
      ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
    };
  };

  const loadDataSequentially = async () => {
    try {
      // First load order data to get delivery type and hub info
      await fetchOrderData();
      // Then load date data and process any URL parameters with full context
      await fetchAvailableDates();
    } catch (err) {
    }
  };

  const fetchOrderData = async () => {
    const finalUrl = `${API_URL}/store/orders/${orderId}/`;

    try {
      const response = await fetch(finalUrl, {
        headers: buildAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch order data');
      }

      const data = await response.json();
      const order = data.order || data;
      
      setOrderData(order);
      
      const scheduleOrderSnapshot = (() => {
        try {
          return JSON.parse(JSON.stringify(order));
        } catch (e) {
          return order;
        }
      })();

      
      const savedIsHubValue =
        (order?.delivery_type || '').toUpperCase() === 'HUB' ||
        !!order?.hub_address ||
        /hub/i.test(order?.shipping_method || '') ||
        /hub/i.test(order?.delivery_type_label || '');

      setSavedIsHub(savedIsHubValue);

      // Hydrate delivery type from existing order
      if (savedIsHubValue) {
        setSelectedDeliveryType('HUB');
        // preserve selected hub id if API sent one; null is OK (tracking will provide details)
        setSelectedHubId(order?.hub_id ?? null);
      } else if (order.delivery_type) {
        setSelectedDeliveryType(order.delivery_type); // HOME from backend
      }

      // Handle hub data hydration for existing hub orders
      if (savedIsHubValue) {
          // Check if we have complete hub data for validation - prioritize hub_address over hub_id
          const hasCompleteHubData = order.hub_address && 
                                   (order.hub_address.hub_name || order.hub_address.address_line) && 
                                   order.hub_address.city && 
                                   (order.hub_address.zip || order.hub_address.postal_code);
          
          if (hasCompleteHubData) {
            const hubInfo = {
              locationId: order.hub_id || 'saved_hub_address', // Use hub_id if available, fallback to identifier
              contactAndAddress: {
                contact: {
                  companyName: order.hub_address.hub_name || order.hub_address.address_line || `FedEx Hub`
                },
                address: {
                  streetLines: [order.hub_address.street1 || order.hub_address.address_line, order.hub_address.street2].filter(Boolean),
                  city: order.hub_address.city,
                  stateOrProvinceCode: order.hub_address.state || order.hub_address.state_code,
                  postalCode: order.hub_address.zip || order.hub_address.postal_code
                }
              }
            };
            
            // Ensure proper UI state for existing valid hub
            setSelectedHub(hubInfo);
            setAvailableHubs([hubInfo]);
            setRequiresHubSelection(false);
            
            // Set selectedHubId even if order.hub_id is null - we have valid hub data
            setSelectedHubId(order.hub_id || 'saved_hub_address');
            
            console.log('CustomerSchedule: Hub data hydrated from order:', {
              hasCompleteHubData: true,
              hub_id: order.hub_id,
              hub_address: order.hub_address,
              hubInfo
            });
            
          } else {
            // Incomplete hub data - require re-selection
            setRequiresHubSelection(true);
            console.log('CustomerSchedule: Incomplete hub data, requiring re-selection:', {
              hasCompleteHubData: false,
              hub_id: order.hub_id,
              hub_address: order.hub_address
            });
          }
        }
      
      // After processing order data, fetch hubs if this is an existing hub pickup order
      
      if (order.delivery_type === 'HUB') {
        // Try to get destination ZIP from order or location info we'll fetch later
        const destZip = order.shipping_address?.zip; // Try order's shipping address first
        if (destZip) {
          await fetchAvailableHubs(destZip);
        } else {
          // No destination ZIP available for hub lookup
        }
      }
      
    } catch (err) {
      // Error loading order data - will require manual hub selection
      // Don't fail the whole page for order data - dates are more critical
      // The page will fall back to requiring hub selection if weather conditions require it
    }
  };

  const fetchAvailableDates = async () => {
    const finalUrl = `${API_URL}/store/orders/${orderId}/available-arrival-dates/`;

    try {

      const response = await fetch(
        finalUrl,
        {
          headers: buildAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch available dates');
      }

      const data = await response.json();
      
      // Deep logging for availability data flow analysis
      console.log('[CustomerSchedule] RAW AVAILABILITY', { orderId, response: data });
      
      setAvailableDates(data.dates || []);
      setDatesWithDetails(data.dates_with_details || []);
      setOrderStatus(data.order_status);
      setFacilityName(data.facility_name);
      setWeatherEnabled(data.weather_enabled || false);
      setLocationInfo(data.location_info || {});
      
      // Populate stateRef for temperature checking
      stateRef.current = {
        datesWithDetails: data.dates_with_details || []
      };
      
      // Log the transformed structure fed to the calendar
      const calendarModel = {
        availableDates: data.dates || [],
        datesWithDetails: data.dates_with_details || [],
        weatherEnabled: data.weather_enabled || false,
        locationInfo: data.location_info || {}
      };
      console.log('[CustomerSchedule] CALENDAR MODEL', calendarModel);
      
      
      // Set initial month to first available date if any
      if (data.dates && data.dates.length > 0) {
        // tz-sweep 2026-08-02: parse as LOCAL date — UTC parse of 'YYYY-MM-DD' can land on the previous month
        const firstDate = new Date(data.dates[0] + 'T00:00:00');
        setCurrentMonth(new Date(firstDate.getFullYear(), firstDate.getMonth(), 1));
      }

      // Restore selected date from URL params if available and valid
      const dateParam = searchParams.get('date');
      if (dateParam && (data.dates?.includes(dateParam) || 
          data.dates_with_details?.some(d => d.date === dateParam && d.available))) {
        setSelectedDate(dateParam);
        // Also check if this date requires hub selection
        const dateDetails = data.dates_with_details?.find(d => d.date === dateParam);
        if (dateDetails?.temperatures) {
          // Use unified temperature safety check
          const deliveryMode = (selectedDeliveryType === 'HUB' || orderData?.delivery_type === 'HUB') ? 'hub' : 'residential';
          const tempSafety = checkTemperatureSafety(dateParam, deliveryMode, stateRef);
          const residentialAllowed = tempSafety.safe;
          if (!residentialAllowed) {
            // Check if order is already set up for hub pickup with valid hub data
            const hasValidHubSetup = selectedDeliveryType === 'HUB' && selectedHubId && selectedHub;
            
            if (!hasValidHubSetup) {
              // Order doesn't have complete hub setup - require hub selection
              setRequiresHubSelection(true);
              // Temperature conditions require hub selection
              // Fetch hubs if we have location info
              const shippingZip = data.location_info?.destination?.zip;
              if (shippingZip) {
                fetchAvailableHubs(shippingZip);
              }
            } else {
              // Order already has valid hub pickup - don't force reselection despite unsafe residential temps
              setRequiresHubSelection(false);
            }
          }
        }
      }
      
      // Also check if this is an existing hub pickup order and fetch hubs accordingly
      // This handles the case where fetchOrderData didn't have the destination ZIP
      if ((selectedDeliveryType === 'HUB' || orderData?.delivery_type === 'HUB') && data.location_info?.destination?.zip) {
        await fetchAvailableHubs(data.location_info.destination.zip);
      }
      
    } catch (err) {
      setError('Failed to load available dates');
    } finally {
      setLoading(false);
    }
  };


  // Legacy function for backward compatibility
  const checkResidentialDelivery = (dateStr) => {
    const result = checkTemperatureSafety(dateStr, 'residential', stateRef);
    return result.safe;
  };

  const validateExistingHubForDate = (dateStr, hubInfo) => {
    // Check if the existing hub is still valid for the selected date's constraints
    if (!hubInfo || !dateStr) return { valid: false, reason: 'missing hub or date' };
    
    const dateDetails = datesWithDetails.find(d => d.date === dateStr);
    if (!dateDetails || !dateDetails.temperatures) {
      // If no temperature data, assume hub is valid (no constraint to check)
      return { valid: true, reason: 'no temperature constraints' };
    }
    
    // For now, we assume any existing hub is valid for any temperature conditions
    // In the future, this could check specific hub ZIP constraints
    const hubZip = hubInfo.contactAndAddress?.address?.postalCode;
    // Standardize on nested destination object
    const destZip = locationInfo?.destination?.zip || locationInfo?.destination_zip || null;
    
    if (hubZip && destZip && hubZip !== destZip) {
      // Hub ZIP differs from destination - this might need validation
    }
    
    // Currently, we trust that existing hubs are valid unless we get specific route/constraint errors
    return { valid: true, reason: 'existing hub assumed valid' };
  };

  const fetchAvailableHubs = async (zip) => {
    
    // Increment sequence counter for this request
    const seq = ++hubsReqSeqRef.current;
    
    // Abort any previous hub fetch request
    if (hubsAbortRef.current) {
      hubsAbortRef.current.abort();
    }
    hubsAbortRef.current = new AbortController();
    
    if (!isMounted.current) return;
    setLoadingHubs(true);
    setError(null); // Clear any previous errors
    
    try {
      // RIDER 2 fix (Cowork 2026-08-04): buildAuthHeaders, NOT a raw
      // localStorage read — token-mode visitors have no authToken, and the
      // "Bearer null" header 401'd at the JWT layer, so hubs never loaded
      // and the hub picker was dead on hub-required dates.
      const response = await fetch(`${API_URL}/shipping/fedex/hub_list_by_zip/?zip=${zip}`, {
        headers: buildAuthHeaders(),
        signal: hubsAbortRef.current.signal,
      });
      
      // Check if this response is still current
      if (seq !== hubsReqSeqRef.current) {
        return;
      }
      
      if (response.status === 401) {
        // Handle auth loss gracefully without masking UI
        if (!isMounted.current) return;
        setError('Session expired — please log in again');
        setAvailableHubs([]); // Keep UI visible but empty
        return;
      }
      
      if (!response.ok) {
        throw new Error(`Failed to fetch hubs: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Check again if this response is still current after await
      if (seq !== hubsReqSeqRef.current) {
        return;
      }
      
      // Debug: Log first hub structure if available
      if (Array.isArray(data) && data.length > 0) {
        // First hub location data available
      }
      
      // Backend already filters for HOLD_AT_LOCATION hubs, so minimal filtering needed
      // Apply same approach as CheckoutAddressPage (no filtering, just display all returned hubs)
      // API structure: hub.contactAndAddress.contact.companyName, hub.contactAndAddress.address
      const validHubs = Array.isArray(data) ? data.filter(hub => {
        // Only reject hubs that would crash the UI
        if (!hub || !hub.locationId) {
          // Invalid hub data - skip
          return false;
        }
        
        // Check for displayable name and address (allow fallbacks)
        const hasDisplayName = !!(hub.contactAndAddress?.contact?.companyName || 
                                 hub.contactAndAddress?.addressAncillaryDetail?.displayName ||
                                 hub.locationType);
        const hasAddress = !!(hub.contactAndAddress?.address);
        
        if (!hasDisplayName || !hasAddress) {
          // Hub missing displayable name or address
          return false;
        }
        
        // Hub has valid display data
        return true;
      }).sort((a, b) => {
        // Sort by distance ascending (closest first)
        const distA = a.distance?.value || 999;
        const distB = b.distance?.value || 999;
        return distA - distB;
      }).slice(0, 10) : []; // Take top 10 closest hubs
      
      if (validHubs.length > 0) {
        const hubSummary = validHubs.map(h => ({ 
          id: h.locationId, 
          name: h.contactAndAddress?.contact?.companyName || 
                h.contactAndAddress?.addressAncillaryDetail?.displayName ||
                h.locationType || 'FedEx Hub',
          city: h.contactAndAddress?.address?.city,
          distance: h.distance?.value
        }));
      } else {
        if (Array.isArray(data) && data.length > 0) {
        }
      }
      // Preserve any stored hub that was set during order data hydration
      const existingStoredHub = availableHubs.find(hub => hub.locationId === selectedHubId);
      const hubsToSet = existingStoredHub ? 
        [existingStoredHub, ...validHubs.filter(hub => hub.locationId !== selectedHubId)] : 
        validHubs;
      
      if (!isMounted.current) return;
      setAvailableHubs(hubsToSet);
      
    } catch (err) {
      // Handle abort silently
      if (err.name === 'AbortError') {
        return;
      }
      
      
      // Only show error UI if component is still mounted
      if (!isMounted.current) return;
      
      // Show toast-style error instead of persistent error state
      const toastError = document.createElement('div');
      toastError.className = 'fixed top-4 right-4 bg-red-500 text-white p-3 rounded-lg shadow-lg z-50 animate-bounce';
      toastError.textContent = 'Failed to load nearby FedEx hubs. Please try selecting the date again.';
      document.body.appendChild(toastError);
      setTimeout(() => {
        if (document.body.contains(toastError)) {
          document.body.removeChild(toastError);
        }
      }, 5000);
      
      // On error, preserve any stored hub that was set during order data hydration
      const existingStoredHub = availableHubs.find(hub => hub.locationId === selectedHubId);
      setAvailableHubs(existingStoredHub ? [existingStoredHub] : []);
    } finally {
      if (!isMounted.current) return;
      setLoadingHubs(false);
    }
  };

  const handleDateSelect = async (date) => {
    // Use timezone-safe date formatting to avoid UTC conversion issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    
    // Check if date is available (either in simple list or detailed list)
    const isAvailable = availableDates.includes(dateStr) || 
      datesWithDetails.some(d => d.date === dateStr && d.available);
    
    if (isAvailable) {
      setSelectedDate(dateStr);
      
      // Check if order is already a hub order
      const isHubOrder = orderData?.delivery_type === 'HUB';
      
      // Check if this date requires hub selection from API data.
      // ROOT CAUSE FIX (Cowork 2026-08-04): the availability API field is
      // `requires_hub` — this read `hub_required` (wrong name, always
      // undefined), so the click-path hub gate NEVER fired in any mode.
      // The account path only appeared to work via the ?date= URL-param
      // flow, which derives the requirement from temperatures instead.
      const dateDetails = getDateDetails(dateStr);
      const isHubRequiredForDate = dateDetails?.requires_hub ?? dateDetails?.hub_required;
      
      // Only show hub gate if NOT already a hub order AND hub is required
      if (!isHubOrder && isHubRequiredForDate) {
        // Check if order is already set up for hub pickup with valid hub data
        const hasValidHubSetup = selectedDeliveryType === 'HUB' && (selectedHubId || orderData?.hub_id || orderData?.hub_address) && (selectedHub || orderData?.hub_address);
        
        if (!hasValidHubSetup) {
          // Order doesn't have complete hub setup - require hub selection
          setRequiresHubSelection(true);
          // Temperature conditions require hub selection
          
          // Always fetch available hubs for hub pickup flow
          // Standardize on nested destination object
          const destZip = locationInfo?.destination?.zip || locationInfo?.destination_zip || null;
          if (destZip) {
            await fetchAvailableHubs(destZip);
          } else {
            setError('Unable to find nearby hubs - missing delivery ZIP code');
          }
          
          // Clear selection to force user to pick
          setSelectedHub(null);
        } else {
          // Order already has hub pickup set up - validate existing hub for this date
          const hubValidation = validateExistingHubForDate(dateStr, selectedHub);
          if (!hubValidation.valid) {
            // Existing hub is no longer valid for this date
            setRequiresHubSelection(true);
            setSelectedHub(null); // Clear invalid hub
            
            // Fetch new hubs
            // Standardize on nested destination object
            const destZip = locationInfo?.destination?.zip || locationInfo?.destination_zip || null;
            if (destZip) {
              await fetchAvailableHubs(destZip);
            }
          } else {
            // Order already has valid hub pickup - don't force reselection despite unsafe residential temps
            setRequiresHubSelection(false);
          }
        }
      } else {
        // Residential delivery is fine OR this is already a hub order
        setRequiresHubSelection(false);
        // Temperature conditions allow residential delivery
        // Only clear selectedHub if this isn't an existing hub pickup order
        if (selectedDeliveryType !== 'HUB' || !selectedHubId) {
          setSelectedHub(null);
        }
      }
      
      // Final safety check: never require hub selection for orders that already have hub data
      if (isHubOrder && (orderData?.hub_id || orderData?.hub_address)) {
        console.log('CustomerSchedule: Hub order with saved hub data detected, skipping hub selection requirement');
        setRequiresHubSelection(false);
      }
      
      console.log('CustomerSchedule handleDateSelect:', {
        isHubOrder,
        orderData_delivery_type: orderData?.delivery_type,
        orderData_hub_id: orderData?.hub_id,
        isHubRequiredForDate,
        requiresHubSelection
      });
    }
  };

  const handleSubmit = async () => {
    // Prevent double submissions
    if (isSubmittingRef || submitting) {
      return;
    }

    if (!selectedDate) {
      setError('Please select an arrival date');
      return;
    }
    
    // Only validate hub selection when requiresHubSelection is true and no stored hub exists
    // Skip validation if order already has a saved hub_id OR hub_address OR selectedHub/selectedHubId
    const hasValidHubData = orderData?.hub_id || orderData?.hub_address || selectedHub || selectedHubId;
    if (requiresHubSelection && !hasValidHubData) {
      // Hub selection required but missing
      setError('Please select a FedEx hub for pickup');
      return;
    }

    const finalUrl = `${API_URL}/store/orders/${orderId}/schedule/`;

    // Determine delivery type - use existing order type unless weather forces hub selection
    const willBeHubDelivery = orderData?.delivery_type === 'HUB' || selectedDeliveryType === 'HUB' || requiresHubSelection;
    const hubIdToUse = orderData?.hub_id || selectedHubId || selectedHub?.locationId;
    
    const requestBody = {
      arrival_date: selectedDate,
      // M1: capability token travels in the POST body (never the URL)
      ...(schedulingToken ? { scheduling_token: schedulingToken } : {}),
      delivery_type: normalizeDeliveryType(willBeHubDelivery ? 'hub_pickup' : 'residential'),
      is_residential: !willBeHubDelivery,
      ...(willBeHubDelivery && (hubIdToUse || orderData?.hub_address) ? { 
        hub_data: {
          hub_id: hubIdToUse || null // Use existing hub_id if available, otherwise let API handle it
        }
      } : {
        // Explicitly clear hub_id when switching to home delivery
        hub_data: {
          hub_id: null
        }
      })
    };
    
    setIsSubmittingRef(true);
    setSubmitting(true);
    setError(null);
    try {
      
      const response = await fetch(
        finalUrl,
        {
          method: 'POST',
          headers: {
            ...buildAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        }
      );

      // RIDER 1 (Cowork 2026-08-04): a 5xx returns an HTML error page —
      // response.json() then throws "Unexpected token '<'" at the customer.
      // Parse defensively and show a human message instead.
      let data = null;
      try {
        data = await response.json();
      } catch (parseErr) {
        data = null;
      }

      if (response.status === 401) {
        // Handle session expiry with inline banner instead of error state
        setSessionExpired(true);
        setSubmitting(false);
        return;
      }

      // Server-side weather-gate enforcement (2026-08-04): typed 409s
      if (response.status === 409 && data?.error === 'hub_required') {
        setRequiresHubSelection(true);
        const destZip = locationInfo?.destination?.zip || locationInfo?.destination_zip || null;
        if (destZip) fetchAvailableHubs(destZip);
        setError(data.message || 'This date requires FedEx hub pickup due to temperatures — please select a hub below.');
        setSubmitting(false);
        setIsSubmittingRef(false);
        return;
      }
      if (response.status === 409 && data?.error === 'weather_blocked') {
        setError(data.message || 'This date is no longer available due to weather. Please pick another date.');
        setSelectedDate(null);
        fetchAvailableDates();
        setSubmitting(false);
        setIsSubmittingRef(false);
        return;
      }

      if (!response.ok || data === null) {
        console.warn('[CustomerSchedule] schedule POST failed with status', response.status);
        throw new Error(
          (data && (data.message || data.error)) ||
          'Something went wrong scheduling your delivery — please try again or contact us.'
        );
      }

      setSuccess(true);
      
      // Show success message with delivery date
      const deliveryDate = data.delivery_date;
      const formattedDate = deliveryDate ? 
        new Date(deliveryDate + 'T00:00:00').toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long', 
          day: 'numeric'
        }) : 'your selected date';
      
      if (schedulingToken) {
        // Token mode: no account pages to navigate to — show an in-page
        // confirmation instead (punch #44b)
        setTokenScheduleSuccess(formattedDate);
        setSubmitting(false);
        setIsSubmittingRef(false);
        return;
      }

      // Brief delay to show success, then navigate to confirmation page
      setTimeout(() => {
        navigate(`/account/orders/${orderId}/scheduled`, {
          state: {
            delivery_date: deliveryDate,
            toast_message: `✅ Delivery scheduled for ${formattedDate}`
          }
        });
      }, 1000);
    } catch (err) {
      setError(err.message || 'Failed to schedule delivery');
      setSubmitting(false);
      setIsSubmittingRef(false);
    }
  };

  const changeMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(newMonth.getMonth() + direction);
      return newMonth;
    });
  };

  const getDateDetails = (dateStr) => {
    // Find detailed date info if available
    return datesWithDetails.find(d => d.date === dateStr) || null;
  };

  const formatTemperature = (temp) => {
    if (temp === null || temp === undefined) return '?';
    return Math.round(temp) + '°F';
  };

  const renderTemperatureData = (dateDetails) => {
    // Always show temperature chips if we have dateDetails with temperatures
    if (!dateDetails || !dateDetails.temperatures) {
      return null;
    }

    const temps = dateDetails.temperatures;
    
    const formatTempRange = (low, high) => {
      if (low === null && high === null) return '?';
      if (low === null) return formatTemperature(high);
      if (high === null) return formatTemperature(low);
      if (low === high) return formatTemperature(high);
      return `${formatTemperature(high)}/${formatTemperature(low)}`;  // tz/wx sweep: formatter already appends °F
    };

    // Per-leg, because the residential hot ceiling is per-leg. A blanket
    // "40–85" on the origin chip is what made an 89°F origin read as the
    // blocker when the origin limit is actually 90.
    const getSafeRangeText = (leg) =>
      orderData?.delivery_type === 'HUB'
        ? `Safe range is ${TEMP_LIMITS.HUB_MIN}°F–${TEMP_LIMITS.HUB_MAX}°F`
        : `Safe range here is ${TEMP_LIMITS.RESIDENTIAL_MIN}°F–${residentialHotLimit(leg)}°F`;
    
    return (
      <div className="cs-temp-chips">
        {/* Origin (O) - Ship date */}
        <div className="temp-chip origin" title={`Origin (ship day at facility): High ${formatTemperature(temps.origin?.high)}, Low ${formatTemperature(temps.origin?.low)} - ${getSafeRangeText('origin')}`} style={{ fontWeight: 'bold' }}>
          O: {formatTempRange(temps.origin?.low, temps.origin?.high)}
        </div>
        {/* Midpoint (M) - Arrival date */}
        <div className="temp-chip midpoint" title={`Midpoint hub (arrival day): High ${formatTemperature(temps.midpoint?.high)}, Low ${formatTemperature(temps.midpoint?.low)} - ${getSafeRangeText('midpoint')}`} style={{ fontWeight: 'bold' }}>
          M: {formatTempRange(temps.midpoint?.low, temps.midpoint?.high)}
        </div>
        {/* Destination (D) - Arrival date.
            Cowork stress-test fix (2026-08-03): hub-required dates show BOTH
            numbers with role labels — the full-day temp explains WHY home
            delivery is restricted (day high vs the 85°F cap), the morning
            pickup-window temp explains why hub pickup is still safe. Showing
            only the pickup temp made the restriction look arbitrary. */}
        {dateDetails.requires_hub && temps.destination_pickup_window ? (
          <div className="temp-chip destination temp-chip--hub-warn" title={`Destination (arrival day at your address): full day ${formatTemperature(temps.destination?.high)}/${formatTemperature(temps.destination?.low)} (home delivery unsafe above ${TEMP_LIMITS.RESIDENTIAL_MAX_DESTINATION}°F or below ${TEMP_LIMITS.RESIDENTIAL_MIN}°F on arrival) · morning pickup window (6-10am): ${formatTemperature(temps.destination_pickup_window?.high)}/${formatTemperature(temps.destination_pickup_window?.low)} — safe for hub pickup (${TEMP_LIMITS.HUB_MIN}°F–${TEMP_LIMITS.HUB_MAX}°F)`} style={{ fontWeight: 'bold' }}>
            D: {formatTempRange(temps.destination?.low, temps.destination?.high)}
          </div>
        ) : (
          <div className={`temp-chip destination${dateDetails.requires_hub ? ' temp-chip--hub-warn' : ''}`} title={`Destination (arrival day at your address): High ${formatTemperature(temps.destination?.high)}, Low ${formatTemperature(temps.destination?.low)} - ${getSafeRangeText('destination')}`} style={{ fontWeight: 'bold' }}>
            D: {formatTempRange(temps.destination?.low, temps.destination?.high)}
          </div>
        )}
      </div>
    );
  };

  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const weeks = [];
    const currentDate = new Date(startDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    while (currentDate <= lastDay || currentDate.getDay() !== 0) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date(currentDate);
        // Use timezone-safe date formatting
        const dateYear = date.getFullYear();
        const dateMonth = String(date.getMonth() + 1).padStart(2, '0');
        const dateDay = String(date.getDate()).padStart(2, '0');
        const dateStr = `${dateYear}-${dateMonth}-${dateDay}`;
        const isCurrentMonth = date.getMonth() === month;
        const dateDetails = getDateDetails(dateStr);
        
        // Check if date is in the past
        const isPast = date < today;
        
        // Check availability - trust API completely, only gate by past dates
        let isAvailable = !isPast;
        if (dateDetails) {
          // Use detailed data if available (trust API decision)
          isAvailable = isAvailable && !!dateDetails.available;
        } else {
          // Fall back to basic list only if no detailed data
          isAvailable = isAvailable && availableDates.includes(dateStr);
        }
        
        const isSelected = selectedDate === dateStr;
        
        // Use new API fields for temperature evaluation
        let isHubRequired = false;
        let isTrulyBlocked = false;
        let isCutoffBlocked = dateDetails && dateDetails.cutoff_blocked;
        
        if (dateDetails) {
          // Use the new backend fields directly
          isTrulyBlocked = dateDetails.weather_blocked || false;
          isHubRequired = dateDetails.requires_hub || false;
        }
        
        // Determine if date is uncertain (no weather data available)
        // Use backend's blocking_key to identify no weather data scenarios
        const isUncertain = isAvailable && dateDetails && 
          (dateDetails.blocking_key === 'no_weather_data' || dateDetails.blocking_key === 'midpoint_zip_unresolved') &&
          !isTrulyBlocked && !isPast;

        week.push({
          date,
          dateStr,
          isCurrentMonth,
          isAvailable,
          isSelected,
          isHubRequired,
          isTrulyBlocked,
          isCutoffBlocked,
          isUncertain,
          isPast,
          day: date.getDate(),
          dateDetails,
        });

        currentDate.setDate(currentDate.getDate() + 1);
      }
      weeks.push(week);
    }

    return weeks;
  };

  // Token mode (punch #44b): invalid/expired capability link
  if (tokenInvalid) {
    return (
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="card shadow">
              <div className="card-body">
                <h2 className="card-title">Schedule Delivery</h2>
                <p className="text-muted mb-1">This scheduling link isn't valid.</p>
                <p className="text-muted">
                  Please check the link you were sent, or contact the store for a new one.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Token mode: in-page confirmation after a successful schedule
  if (tokenScheduleSuccess) {
    return (
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="card shadow">
              <div className="card-body text-center">
                <h2 className="card-title">✅ Delivery Scheduled</h2>
                <p className="text-muted">
                  Your delivery is scheduled for <strong>{tokenScheduleSuccess}</strong>.
                </p>
                <p className="text-muted">
                  You'll receive email updates as your order is prepared and shipped.
                  Keep this link if you need to reschedule.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container">
        <div className="text-center py-5">
          <FroggySpinner size="lg" label="Loading your schedule" />
        </div>
      </div>
    );
  }

  // Token mode may also (re)schedule from AWAITING_STORE_SCHEDULING —
  // manual orders are born in that status (product decision, Cowork run 3).
  // Account mode keeps the original two-status gate.
  const schedulableStatuses = ['AWAITING_CUSTOMER_SCHEDULING', 'REQUIRES_CUSTOMER_RESCHEDULING_DUE_TO_WEATHER'];
  if (schedulingToken) schedulableStatuses.push('AWAITING_STORE_SCHEDULING');
  if (!schedulableStatuses.includes(orderStatus)) {
    return (
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="card shadow">
              <div className="card-body">
                <h2 className="card-title">Schedule Delivery</h2>
                {orderStatus ? (
                  <p className="text-muted">
                    This order cannot be scheduled. Current status: {orderStatus}
                  </p>
                ) : (
                  // Empty/unknown status (e.g. the order API 404'd for an
                  // order this account doesn't own) — avoid a dangling
                  // "Current status:" with no value
                  <>
                    <p className="text-muted mb-1">This order cannot be scheduled.</p>
                    <p className="text-muted">It may belong to a different account.</p>
                  </>
                )}
                {/* Token-mode visitors have no account order page */}
                {!schedulingToken && (
                  <button
                    type="button"
                    onClick={() => navigate(`/account/orders/${orderId}`)}
                    className="ff-btn ff-btn--primary mt-3"
                  >
                    View Order Details
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Calculate next available date
  const getNextAvailableDate = () => {
    if (availableDates.length === 0) return null;
    const sortedDates = [...availableDates].sort();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (const dateStr of sortedDates) {
      const date = new Date(dateStr + 'T00:00:00');
      if (date >= today) {
        return dateStr;
      }
    }
    return null;
  };

  const nextAvailable = getNextAvailableDate();

  return (
    <div className="shipping-calendar-wrapper">
      <div className="shipping-calendar">
      {/* Screen reader live region for announcements */}
      <div 
        aria-live="polite" 
        aria-atomic="true" 
        className="sr-only"
        id="calendar-announcements"
      >
        {announcement}
      </div>
      <div className="max-w-5xl mx-auto">
        <div>
          <div className="cs-header text-center">
            <h1 className="cs-title">
              {orderStatus === 'REQUIRES_CUSTOMER_RESCHEDULING_DUE_TO_WEATHER'
                ? 'Reschedule your arrival date'
                : 'Select your arrival date'}
            </h1>
            <p className="cs-subtitle">
              {orderStatus === 'REQUIRES_CUSTOMER_RESCHEDULING_DUE_TO_WEATHER'
                ? '🌡️ Your original delivery date has unsafe temperatures. Please choose a new safe arrival date below.'
                : 'We ship Priority Overnight. You\'re picking the arrival date.'}
            </p>
            {facilityName && (
              <p className="cs-facility">
                Shipping from: {facilityName}
              </p>
            )}
          </div>

          {nextAvailable && (
            <div className="cs-banner cs-banner--success">
              <p className="mb-0">
                ✅ Next available date: {new Date(nextAvailable + 'T00:00:00').toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          )}

          {error && (
            <div className="cs-banner cs-banner--danger">
              {error}
            </div>
          )}

          {success && (
            <div className="cs-banner cs-banner--success">
              Arrival date saved! Redirecting...
            </div>
          )}

          {/* Calendar Legend — compact chip row directly above the calendar */}
          <div className="cs-legend" role="group" aria-label="Calendar legend">
            <span className="cs-legend-chip">
              <span className="cs-legend-swatch cs-legend-swatch--available"></span>
              Available
            </span>
            <span className="cs-legend-chip">
              <span className="cs-legend-swatch cs-legend-swatch--hub"></span>
              🏢 Hub pickup required
            </span>
            <span className="cs-legend-chip">
              <span className="cs-legend-swatch cs-legend-swatch--blocked"></span>
              🚫 Weather blocked
            </span>
            <span className="cs-legend-chip">
              <span className="cs-legend-swatch cs-legend-swatch--cutoff"></span>
              ⏰ Past cutoff
            </span>
            <span className="cs-legend-chip">
              <span className="cs-legend-swatch cs-legend-swatch--nodata"></span>
              ⚠️ No weather data
            </span>
            <span className="cs-legend-chip">
              <span className="cs-legend-swatch cs-legend-swatch--past"></span>
              Past / unavailable
            </span>
            <span className="cs-legend-chip">
              <span className="cs-legend-swatch cs-legend-swatch--selected"></span>
              Selected
            </span>
          </div>

          {/* Shipping Policy Note (copy verbatim — collapsible, open by default) */}
          <details className="cs-policy-note" open>
            <summary>
              <span className="me-2">ℹ️</span>
              Available – No Weather Data Policy
            </summary>
            <p className="mb-0 small">
              These dates are fully orderable for residential delivery, but weather data has not been confirmed yet. Conditions will be re-checked closer to your ship date. If conditions become unsafe, we'll email you with options to reschedule or switch to FedEx hub pickup.
              Your order will not ship until safe delivery conditions are confirmed.
            </p>
          </details>

          <div className="cs-calendar mb-6">
            <div className="calendar-header d-flex justify-content-between align-items-center">
              <button
                onClick={() => changeMonth(-1)}
                className="calendar-nav-btn"
                aria-label="Previous month"
              >
                ‹
              </button>
              <h3 className="calendar-month mb-0">
                {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h3>
              <button
                onClick={() => changeMonth(1)}
                className="calendar-nav-btn"
                aria-label="Next month"
              >
                ›
              </button>
            </div>

            <div className="calendar-grid" role="grid" aria-label="Calendar for selecting arrival date">
              <div className="calendar-weekdays" role="row">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="calendar-weekday">
                    {day}
                  </div>
                ))}
              </div>

              <div className="calendar-days">
                {renderCalendar().map((week, weekIndex) => (
                  <div key={weekIndex} className="calendar-week" role="row">
                    {week.map(({ date, dateStr, isCurrentMonth, isAvailable, isSelected, isHubRequired, isTrulyBlocked, isCutoffBlocked, isUncertain, isPast, day, dateDetails }) => {
                      // Determine tooltip text with hub and destination info
                      let tooltipText = '';

                      // Derive hasTempData and requiresHub flags
                      // Use != null to check for both null and undefined
                      const temps = dateDetails?.temperatures;
                      const hasTempData = !!(temps && (
                        temps.origin?.high != null ||
                        temps.midpoint?.high != null ||
                        temps.destination?.high != null
                      ));
                      const requiresHub = isHubRequired;

                      if (!isCurrentMonth) {
                        const monthName = date.toLocaleDateString('en-US', { month: 'long' });
                        tooltipText = `Click to jump to ${monthName}`;
                      } else if (isCutoffBlocked && dateDetails?.blocking_reason) {
                        tooltipText = dateDetails.blocking_reason;
                      } else if (isTrulyBlocked && dateDetails?.blocking_reason) {
                        tooltipText = dateDetails.blocking_reason;
                      } else if (isPast) {
                        tooltipText = 'Date has passed';
                      } else if (dateDetails?.blocking_key === 'no_weather_data') {
                        // No weather data available - still available, but explain missing weather data
                        const reason = dateDetails.blocking_reason ||
                          'No weather data available for this date yet. Conditions will be re-checked closer to your ship date.';
                        // Strip any existing "Available for delivery" prefix to avoid duplication
                        const cleanReason = reason.replace(/^Available for delivery\.?\s*/i, '');
                        tooltipText = `Available for delivery. ${cleanReason}`;
                      } else if (hasTempData && temps) {
                        // Show temps for both hub and residential days - include all three checkpoints
                        const originInfo = locationInfo?.origin_name || 'Origin';
                        const hubInfo = locationInfo?.hub_name ? `${locationInfo.hub_name} hub` : 'Memphis hub';
                        const destInfo = locationInfo?.destination_zip ? `your location (${locationInfo.destination_zip})` : 'your location';

                        let tempDetails = [];
                        // Always include origin temps first
                        if (temps.origin) {
                          tempDetails.push(`${originInfo}: ${formatTemperature(temps.origin.high)}/${formatTemperature(temps.origin.low)}`);
                        }
                        // Then midpoint (hub)
                        if (temps.midpoint) {
                          tempDetails.push(`${hubInfo}: ${formatTemperature(temps.midpoint.high)}/${formatTemperature(temps.midpoint.low)}`);
                        }
                        // Finally destination — hub-required dates show BOTH
                        // numbers with role labels (Cowork stress-test fix:
                        // full day = why home delivery is restricted, morning
                        // window = why hub pickup is still safe)
                        if (requiresHub && temps.destination_pickup_window) {
                          const fullDayPart = temps.destination
                            ? `full day ${formatTemperature(temps.destination.high)}/${formatTemperature(temps.destination.low)} (home delivery unsafe above ${TEMP_LIMITS.RESIDENTIAL_MAX_DESTINATION}°F or below ${TEMP_LIMITS.RESIDENTIAL_MIN}°F on arrival) · `
                            : '';
                          tempDetails.push(`${destInfo}: ${fullDayPart}morning pickup window (6-10am): ${formatTemperature(temps.destination_pickup_window.high)}/${formatTemperature(temps.destination_pickup_window.low)} — safe for hub pickup`);
                        } else if (temps.destination) {
                          tempDetails.push(`${destInfo}: ${formatTemperature(temps.destination.high)}/${formatTemperature(temps.destination.low)}`);
                        }

                        let baseText = tempDetails.length > 0 ?
                          `Available for delivery. Temps - ${tempDetails.join('; ')}` :
                          'Available for delivery';

                        // Add hub requirement if applicable
                        if (requiresHub) {
                          baseText += '. Hub pickup required due to temperature conditions';
                        }

                        tooltipText = baseText;
                      } else if (isAvailable) {
                        tooltipText = 'Available for delivery';
                      }

                      // Popover handlers
                      const handlePopoverToggle = (e) => {
                        e.stopPropagation();
                        setPopoverOpen(popoverOpen === dateStr ? null : dateStr);
                      };

                      const handleMouseEnter = () => {
                        if (!('ontouchstart' in window)) {
                          setPopoverOpen(dateStr);
                        }
                      };

                      const handleMouseLeave = () => {
                        if (!('ontouchstart' in window)) {
                          setPopoverOpen(null);
                        }
                      };

                      const handleFocus = () => {
                        setPopoverOpen(dateStr);
                      };

                      const handleBlur = () => {
                        setPopoverOpen(null);
                      };

                      const handleKeyDown = (e) => {
                        if (e.key === 'Escape') {
                          setPopoverOpen(null);
                        }
                      };

                      return (
                        <button
                          key={dateStr}
                          onClick={(e) => {
                            // Toggle popover on touch devices
                            if ('ontouchstart' in window && isCurrentMonth && (isUncertain || isHubRequired)) {
                              handlePopoverToggle(e);
                            }

                            // Handle touch tooltip logic for other-month
                            const currentCount = clickCount[dateStr] || 0;
                            if ('ontouchstart' in window && !isCurrentMonth) {
                              if (currentCount === 0) {
                                // First tap - show tooltip
                                setTouchTooltipActive(dateStr);
                                setClickCount({...clickCount, [dateStr]: 1});
                                setTimeout(() => {
                                  setTouchTooltipActive(null);
                                  setClickCount({...clickCount, [dateStr]: 0});
                                }, 3000); // Hide tooltip after 3 seconds
                                return;
                              }
                              // Second tap - proceed with action
                              setTouchTooltipActive(null);
                              setClickCount({...clickCount, [dateStr]: 0});
                            }

                            if (!isCurrentMonth) {
                              // Jump to the month of this date
                              const monthName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                              setCurrentMonth(new Date(date.getFullYear(), date.getMonth(), 1));
                              // Also select the date if it's available
                              if (isAvailable && !isTrulyBlocked && !isCutoffBlocked) {
                                setTimeout(() => {
                                  handleDateSelect(date);
                                  // Announce for screen readers
                                  const announcement = `Jumped to ${monthName}, date ${date.getDate()} selected`;
                                  setAnnouncement(announcement);
                                  setTimeout(() => setAnnouncement(''), 1000);
                                }, 50); // Small delay to ensure month change processes first
                              } else {
                                // Just announce the month change
                                const announcement = `Jumped to ${monthName}`;
                                setAnnouncement(announcement);
                                setTimeout(() => setAnnouncement(''), 1000);
                              }
                            } else if (isAvailable && !isTrulyBlocked && !isCutoffBlocked) {
                              handleDateSelect(date);
                              // Announce for screen readers
                              const announcement = `Date ${date.getDate()} selected${isUncertain ? ', weather uncertain' : ''}`;
                              setAnnouncement(announcement);
                              setTimeout(() => setAnnouncement(''), 1000);
                            }
                          }}
                          onMouseEnter={isCurrentMonth && (isUncertain || isHubRequired) ? handleMouseEnter : undefined}
                          onMouseLeave={isCurrentMonth && (isUncertain || isHubRequired) ? handleMouseLeave : undefined}
                          onFocus={isCurrentMonth && (isUncertain || isHubRequired) ? handleFocus : undefined}
                          onBlur={isCurrentMonth && (isUncertain || isHubRequired) ? handleBlur : undefined}
                          onKeyDown={handleKeyDown}
                          disabled={isPast || (isCurrentMonth && (isTrulyBlocked || isCutoffBlocked || !isAvailable))}
                          title={
                            // Only suppress title when there's a meaningful popover with temp data
                            // Keep title for no-weather-data days so users see the explanation
                            isCurrentMonth && isHubRequired && hasTempData ? '' : tooltipText
                          }
                          aria-label={
                            !isCurrentMonth ? `${day}, ${date.toLocaleDateString('en-US', { month: 'long' })}. Click to jump to that month.` :
                            isPast ? `${day}, past date, not available` :
                            isTrulyBlocked ? `${day}, blocked - not shippable` :
                            isHubRequired ? `${day}, available - hub pickup required` :
                            isCutoffBlocked ? `${day}, blocked due to cutoff time` :
                            isUncertain ? `${day}, available but weather uncertain` :
                            isAvailable && dateDetails?.temperatures ? (() => {
                              const temps = dateDetails.temperatures;
                              const hubInfo = locationInfo?.hub_name ? `${locationInfo.hub_name} hub` : 'Processing hub';
                              const destInfo = locationInfo?.destination_zip ? `your location ${locationInfo.destination_zip}` : 'your location';
                              
                              let tempDetails = [];
                              if (temps.midpoint) {
                                tempDetails.push(`${hubInfo} ${formatTemperature(temps.midpoint.high)} to ${formatTemperature(temps.midpoint.low)}`);
                              }
                              if (isHubRequired && temps.destination_pickup_window) {
                                // Dual format: full day explains the home-delivery
                                // restriction, morning window explains hub safety
                                const ariaFullDay = temps.destination
                                  ? `full day ${formatTemperature(temps.destination.high)} to ${formatTemperature(temps.destination.low)}, home delivery unsafe above ${TEMP_LIMITS.RESIDENTIAL_MAX_DESTINATION} degrees on arrival, `
                                  : '';
                                tempDetails.push(`${destInfo} ${ariaFullDay}morning pickup window ${formatTemperature(temps.destination_pickup_window.high)} to ${formatTemperature(temps.destination_pickup_window.low)}, safe for hub pickup`);
                              } else if (temps.destination) {
                                tempDetails.push(`${destInfo} ${formatTemperature(temps.destination.high)} to ${formatTemperature(temps.destination.low)}`);
                              }
                              
                              return `${day}, available for delivery. Temperature ranges: ${tempDetails.join(', ')}`;
                            })() :
                            isAvailable ? `${day}, available for delivery` :
                            `${day}, not available`
                          }
                          aria-pressed={isSelected}
                          role="gridcell"
                          tabIndex={isCurrentMonth && (isAvailable || !isPast) ? 0 : -1}
                          className={`
                            calendar-day
                            ${!isCurrentMonth ? 'other-month' : ''}
                            ${isPast ? 'past-date' : ''}
                            ${touchTooltipActive === dateStr ? 'touch-tooltip-active' : ''}
                            ${isAvailable && !isUncertain && !isTrulyBlocked && !isCutoffBlocked && !isHubRequired ? 'available' : ''}
                            ${isHubRequired ? 'hub-required' : ''}
                            ${isUncertain ? 'uncertain' : ''}
                            ${isTrulyBlocked ? 'weather-blocked' : ''}
                            ${isCutoffBlocked ? 'weather-blocked' : ''}
                            ${!isAvailable && !isPast && !isTrulyBlocked && !isCutoffBlocked ? 'unavailable' : ''}
                            ${isSelected ? 'selected' : ''}
                            ${popoverOpen === dateStr ? 'show-popover' : ''}
                          `}
                        >
                          <div className="cs-day-num font-medium">{day}</div>
                          {isCurrentMonth && isHubRequired && (
                            <div className="cs-tile-status">
                              <small className="fw-bold">Hub required</small>
                            </div>
                          )}
                          {isCurrentMonth && isTrulyBlocked && (
                            <div className="cs-tile-status cs-tile-status--blocked">
                              <div className="cs-tile-emoji">🚫</div>
                              <div className="cs-tile-flag">Blocked</div>
                            </div>
                          )}
                          {isCurrentMonth && isCutoffBlocked && (
                            <div className="cs-tile-status cs-tile-status--blocked cs-tile-emoji">⏰</div>
                          )}
                          {isCurrentMonth && isUncertain && (
                            <div className="cs-tile-status cs-tile-emoji">⚠️</div>
                          )}
                          {!isCurrentMonth && (
                            <div className="cs-tile-othermonth">
                              {date.toLocaleDateString('en-US', { month: 'short' })}
                            </div>
                          )}

                          {/* Popover for yellow days (uncertain or hub-required) */}
                          {isCurrentMonth && (isUncertain || isHubRequired) && (
                            <div className="day-popover" role="dialog" aria-live="polite">
                              <div style={{whiteSpace: 'normal', maxWidth: '250px', textAlign: 'left', color: '#fff', fontSize: '13px'}}>
                                {tooltipText}
                              </div>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {selectedDate && (
            <div className="cs-selected-card">
              <div className="cs-selected-label">Selected arrival date:</div>
              <div className="cs-selected-date">
                {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
              {renderTemperatureData(getDateDetails(selectedDate))}
            </div>
          )}

          {/* Show hub selector for hub orders without saved hub OR when hub is required due to temperature */}
          {(() => {
            // Hub order is valid if it has either hub_id OR hub_address
            const hasValidHubData = orderData?.hub_id || orderData?.hub_address;
            const shouldShowHubSelector = orderData?.delivery_type === 'HUB' && !hasValidHubData && !selectedHubId;
            console.log('CustomerSchedule: Hub selector condition check:', {
              delivery_type: orderData?.delivery_type,
              hub_id: orderData?.hub_id,
              hub_address: orderData?.hub_address,
              hasValidHubData,
              selectedHubId,
              shouldShowHubSelector,
              availableHubsLength: availableHubs.length
            });
            return shouldShowHubSelector;
          })() && (
            <div className="cs-card cs-card--info">
              <div className="cs-card-head">
                <h3 className="cs-card-title">
                  <span className="me-2">🏢</span>
                  Select Your Pickup Hub
                </h3>
                <p className="cs-card-subtitle">
                  Please select a FedEx hub for pickup.
                </p>
              </div>

              {loadingHubs ? (
                <div className="cs-card-note">Loading nearby hubs...</div>
              ) : availableHubs.length > 0 ? (
                <div>
                  <label className="form-label fw-semibold cs-hub-label">
                    Select FedEx Hub:
                  </label>
                  <select
                    value={selectedHub?.locationId || ''}
                    onChange={(e) => {
                      const hubId = e.target.value;
                      const hub = availableHubs.find(h => h.locationId === hubId);
                      setSelectedHub(hub || null);
                      setSelectedHubId(hubId || null);
                    }}
                    className="form-select cs-hub-select"
                  >
                    <option value="">Choose a hub...</option>
                    {availableHubs.map((hub) => {
                      const name = hub.contactAndAddress?.contact?.companyName || 
                                  hub.contactAndAddress?.addressAncillaryDetail?.displayName ||
                                  hub.locationType || 
                                  'FedEx Hub';
                      const street = hub.contactAndAddress?.address?.streetLines?.[0] || 'Address unavailable';
                      const city = hub.contactAndAddress?.address?.city || 'City unavailable';
                      const distance = hub.distance?.value;
                      
                      return (
                        <option key={hub.locationId} value={hub.locationId}>
                          {name} - {street}, {city}
                          {distance && ` (${distance.toFixed(1)} mi)`}
                        </option>
                      );
                    })}
                  </select>
                  
                  {selectedHub && (
                    <div className="cs-subcard mt-3">
                      <h4 className="cs-subcard-title">Selected Hub:</h4>
                      <p className="cs-subcard-text">
                        {selectedHub.contactAndAddress?.contact?.companyName || 
                         selectedHub.contactAndAddress?.addressAncillaryDetail?.displayName ||
                         selectedHub.locationType || 
                         'FedEx Hub'}<br/>
                        {(selectedHub.contactAndAddress?.address?.streetLines || selectedHub.address?.streetLines)?.join(', ') || 'Address unavailable'}<br/>
                        {selectedHub.contactAndAddress?.address?.city || selectedHub.address?.city || 'City unavailable'}, {selectedHub.contactAndAddress?.address?.stateOrProvinceCode || selectedHub.address?.stateOrProvinceCode || 'State unavailable'} {selectedHub.contactAndAddress?.address?.postalCode || selectedHub.address?.postalCode || 'ZIP unavailable'}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="cs-card-note">
                  No nearby FedEx hubs found. Please contact support.
                </div>
              )}
            </div>
          )}

          {/* Show existing hub if hub order already has one saved */}
          {(() => {
            // Show selected hub panel if order has hub_id OR hub_address OR selectedHubId
            const hasValidHubData = orderData?.hub_id || orderData?.hub_address || selectedHubId;
            const shouldShowSelectedHub = orderData?.delivery_type === 'HUB' && hasValidHubData;
            console.log('CustomerSchedule: Selected hub panel condition check:', {
              delivery_type: orderData?.delivery_type,
              hub_id: orderData?.hub_id,
              hub_address: orderData?.hub_address,
              hasValidHubData,
              selectedHubId,
              shouldShowSelectedHub
            });
            return shouldShowSelectedHub;
          })() && (
            <div className="cs-card cs-card--success">
              <div className="cs-card-head">
                <h3 className="cs-card-title">
                  <span className="me-2">✅</span>
                  Selected Hub
                </h3>
              </div>
              <div className="cs-subcard">
                <p className="cs-subcard-text">
                  {/* Show hub info from order data or selected hub object */}
                  {orderData?.hub_id && orderData?.hub_address ? (
                    <>
                      {orderData.hub_address.hub_name || `FedEx Hub ${orderData.hub_id}`}<br/>
                      {orderData.hub_address.street_lines?.join(', ') || orderData.hub_address.address_line || 'Address unavailable'}<br/>
                      {orderData.hub_address.city || 'City unavailable'}, {orderData.hub_address.state_code || 'State unavailable'} {orderData.hub_address.postal_code || 'ZIP unavailable'}
                    </>
                  ) : selectedHub ? (
                    <>
                      {selectedHub.contactAndAddress?.contact?.companyName || 
                       selectedHub.contactAndAddress?.addressAncillaryDetail?.displayName ||
                       selectedHub.locationType || 
                       'FedEx Hub'}<br/>
                      {(selectedHub.contactAndAddress?.address?.streetLines || selectedHub.address?.streetLines)?.join(', ') || 'Address unavailable'}<br/>
                      {selectedHub.contactAndAddress?.address?.city || selectedHub.address?.city || 'City unavailable'}, {selectedHub.contactAndAddress?.address?.stateOrProvinceCode || selectedHub.address?.stateOrProvinceCode || 'State unavailable'} {selectedHub.contactAndAddress?.address?.postalCode || selectedHub.address?.postalCode || 'ZIP unavailable'}
                    </>
                  ) : (
                    <>Hub information will be provided with tracking</>
                  )}
                </p>
              </div>
            </div>
          )}

          {requiresHubSelection && (
            <div className="cs-card cs-card--warning">
              <div className="cs-card-head">
                <h3 className="cs-card-title">
                  <span className="me-2">🏢</span>
                  Hub Pickup Required
                </h3>
                <p className="cs-card-subtitle">
                  Due to temperature conditions on your selected date, residential delivery is not available.
                  You can either select a nearby FedEx hub for pickup, or choose a different arrival date with safer temperatures.
                </p>
              </div>

              {loadingHubs ? (
                <div className="cs-card-note">Loading nearby hubs...</div>
              ) : error && error.includes('Session expired') ? (
                <div className="cs-banner cs-banner--danger mb-3">
                  {error}
                </div>
              ) : availableHubs.length > 0 ? (
                <div>
                  <label className="form-label fw-semibold cs-hub-label">
                    Select FedEx Hub:
                  </label>
                  <select
                    value={selectedHub?.locationId || ''}
                    onChange={(e) => {
                      const hubId = e.target.value;
                      const hub = availableHubs.find(h => h.locationId === hubId);
                      setSelectedHub(hub || null);
                    }}
                    className="form-select cs-hub-select"
                  >
                    <option value="">Choose a hub...</option>
                    {availableHubs.map((hub) => {
                      const name = hub.contactAndAddress?.contact?.companyName || 
                                  hub.contactAndAddress?.addressAncillaryDetail?.displayName ||
                                  hub.locationType || 
                                  'FedEx Hub';
                      const street = hub.contactAndAddress?.address?.streetLines?.[0] || 'Address unavailable';
                      const city = hub.contactAndAddress?.address?.city || 'City unavailable';
                      const distance = hub.distance?.value;
                      
                      return (
                        <option key={hub.locationId} value={hub.locationId}>
                          {name} - {street}, {city}
                          {distance && ` (${distance.toFixed(1)} mi)`}
                        </option>
                      );
                    })}
                  </select>
                  
                  {selectedHub && (
                    <div className="cs-subcard mt-3">
                      <h4 className="cs-subcard-title">Selected Hub:</h4>
                      <p className="cs-subcard-text">
                        {selectedHub.contactAndAddress?.contact?.companyName || 
                         selectedHub.contactAndAddress?.addressAncillaryDetail?.displayName ||
                         selectedHub.locationType || 
                         'FedEx Hub'}<br/>
                        {(selectedHub.contactAndAddress?.address?.streetLines || selectedHub.address?.streetLines)?.join(', ') || 'Address unavailable'}<br/>
                        {selectedHub.contactAndAddress?.address?.city || selectedHub.address?.city || 'City unavailable'}, {selectedHub.contactAndAddress?.address?.stateOrProvinceCode || selectedHub.address?.stateOrProvinceCode || 'State unavailable'} {selectedHub.contactAndAddress?.address?.postalCode || selectedHub.address?.postalCode || 'ZIP unavailable'}
                      </p>
                      {selectedHub.standardTransitTimeInDays && (
                        <p className="cs-subcard-meta mt-1">
                          Estimated transit: {selectedHub.standardTransitTimeInDays} days
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : (() => {
                // Check if we have a previously selected hub
                return selectedHubId && selectedHub;
              })() ? (
                <div>
                  <div className="cs-banner cs-banner--info mb-3">
                    <strong>Using previously selected hub:</strong>
                  </div>
                  <div className="cs-subcard">
                    <h4 className="cs-subcard-title">Selected Hub:</h4>
                    <p className="cs-subcard-text">
                      {selectedHub.contactAndAddress?.contact?.companyName || 
                       selectedHub.contactAndAddress?.addressAncillaryDetail?.displayName ||
                       selectedHub.locationType || 
                       'FedEx Hub'}<br/>
                      {(selectedHub.contactAndAddress?.address?.streetLines || selectedHub.address?.streetLines)?.join(', ') || 'Address unavailable'}<br/>
                      {selectedHub.contactAndAddress?.address?.city || selectedHub.address?.city || 'City unavailable'}, {selectedHub.contactAndAddress?.address?.stateOrProvinceCode || selectedHub.address?.stateOrProvinceCode || 'State unavailable'} {selectedHub.contactAndAddress?.address?.postalCode || selectedHub.address?.postalCode || 'ZIP unavailable'}
                    </p>
                  </div>
                </div>
              ) : (
                <div>
                  {error && !error.includes('Session expired') && (
                    <div className="cs-banner cs-banner--danger mb-3">
                      {error}
                    </div>
                  )}
                  <div className="cs-card-note">
                    No nearby FedEx hubs found. Please try a different date or contact support.
                  </div>
                </div>
              )}

              <div className="cs-card-footer">
                <button
                  onClick={() => {
                    setSelectedDate(null);
                    setRequiresHubSelection(false);
                    setSelectedHub(null);
                  }}
                  className="ff-btn ff-btn--secondary"
                >
                  ← Choose Different Date Instead
                </button>
              </div>
            </div>
          )}

          {sessionExpired && (
            <div className="cs-banner cs-banner--info text-center">
              <span className="fw-semibold">
                Your session expired.
              </span>
              <button
                onClick={() => {
                  const returnUrl = `/account/orders/${orderId}/schedule${selectedDate ? `?date=${selectedDate}` : ''}`;
                  navigate(`/login?redirect=${encodeURIComponent(returnUrl)}`);
                }}
                className="ff-btn ff-btn--link p-0 align-baseline ms-2 cs-inline-link"
              >
                Log in to continue
              </button>
            </div>
          )}

          <div className="cs-actions">
            <div className="cs-actions-bar">
                <button
                  onClick={() => navigate(`/account/orders/${orderId}`)}
                  className="ff-btn ff-btn--secondary"
                >
                  Cancel
                </button>
                <button
              onClick={() => {
                handleSubmit();
              }}
              disabled={(() => {
                if (!selectedDate || submitting) return true;
                
                // Selector only matters if we're showing it and there are choices
                const needsHubChoice = requiresHubSelection && (availableHubs?.length > 0);
                
                // Allow confirm if:
                // - user picked HUB, and (a hub is selected OR a hub choice isn't required OR backend already saved hub info)
                const canConfirm =
                  selectedDeliveryType === 'HUB' &&
                  (selectedHubId || !needsHubChoice || savedIsHub);
                
                return selectedDeliveryType === 'HUB' ? !canConfirm : false;
              })()}
              aria-disabled={(() => {
                if (!selectedDate || submitting) return true;
                const needsHubChoice = requiresHubSelection && (availableHubs?.length > 0);
                const canConfirm = selectedDeliveryType === 'HUB' && (selectedHubId || !needsHubChoice || savedIsHub);
                return selectedDeliveryType === 'HUB' ? !canConfirm : false;
              })()}
              title={
                !selectedDate ? 'Please select an arrival date' :
                ((requiresHubSelection || (orderData?.delivery_type === 'HUB' && !orderData?.hub_id && !orderData?.hub_address)) && !selectedHub && !selectedHubId) ? 'Please select a FedEx hub for pickup' :
                submitting ? 'Saving your selection...' :
                'Click to confirm your selection'
              }
              className="ff-btn ff-btn--primary cs-confirm-btn"
              onFocus={(e) => {
                // Focus ring handled by Tailwind classes
              }}
              onBlur={(e) => {
                // Focus ring handled by Tailwind classes
              }}
            >
              {submitting ? 'Saving...' :
               (requiresHubSelection || orderData?.delivery_type === 'HUB') ? 'Confirm Hub Pickup' :
               'Confirm Arrival Date'}
            </button>
            </div>
          </div>

          {/* Temperature Guide */}
          <div className="cs-card cs-card--info cs-info-card">
            <div className="cs-card-head">
              <h3 className="cs-card-title">
                🌡️ Temperature Guide
              </h3>
            </div>
            <div className="cs-card-body small">
              <div className="mb-1"><strong>O</strong> = Origin (ship day): {locationInfo.origin_name || 'Facility'}
                {locationInfo.origin_city && ` (${locationInfo.origin_city}, ${locationInfo.origin_state})`}
              </div>
              <div className="mb-1"><strong>M</strong> = Midpoint (arrival day): {locationInfo.hub_name || 'Processing Center'} hub</div>
              <div><strong>D</strong> = Destination (arrival day): Your location {locationInfo.destination_zip && `(${locationInfo.destination_zip})`}</div>
            </div>
          </div>

          {/* Temperature Rules */}
          <div className="cs-card cs-card--warning cs-info-card">
            <div className="cs-card-head">
              <h3 className="cs-card-title">
                <span className="me-2">🌡️</span>
                Temperature Safety Rules
              </h3>
            </div>
            <div className="cs-card-body small">
              {/* Windowed weather redesign (2026-08): temps are judged for
                  the actual exposure windows, not the full day's min/max */}
              <p className="text-center mb-2">
                We check temperatures for the times your animal is actually
                exposed: the evening departure at our facility, the
                overnight transit stop, and your delivery window on arrival
                day — for hub pickup that's just the morning pickup window
                (6–10am); for home delivery, the day through about 5pm.
              </p>
              <div className="cs-rule-row">
                <span className="cs-rule-dot cs-rule-dot--safe"></span>
                <span><strong>Home delivery safe:</strong> {TEMP_LIMITS.RESIDENTIAL_MIN}°F – {TEMP_LIMITS.RESIDENTIAL_MAX}°F during each exposure window</span>
              </div>
              <div className="cs-rule-row">
                <span className="cs-rule-dot cs-rule-dot--hub"></span>
                <span><strong>Hub pickup required:</strong> {TEMP_LIMITS.HUB_MIN}°F – {TEMP_LIMITS.RESIDENTIAL_MIN - 1}°F or {TEMP_LIMITS.RESIDENTIAL_MAX + 1}°F – {TEMP_LIMITS.HUB_MAX}°F in a window (the morning pickup avoids afternoon heat)</span>
              </div>
              <div className="cs-rule-row">
                <span className="cs-rule-dot cs-rule-dot--blocked"></span>
                {/* Copy matches the actual backend comparisons (strict
                    > 92.0 / < 30.0 in weather_thresholds.py):
                    92°F itself is still hub-eligible; blocking starts
                    ABOVE 92 / BELOW 30. The old "≥93" wording left
                    92.0-92.9 undocumented. */}
                <span><strong>Shipping blocked:</strong> above {TEMP_LIMITS.HUB_MAX}°F or below {TEMP_LIMITS.HUB_MIN}°F during the relevant windows</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default CustomerSchedule;